/**
 * The top-bar bell and the panel behind it, grouped as the K3 shell groups them: Mijozlar, Operator
 * xabarlari, Vazifalar, Tizim, each with its own "O'qildi".
 *
 * The count polls every minute because it is the only thing visible while the panel is shut. The
 * list is fetched when the panel opens and not before. Critical rows stay first and are labelled
 * in words, never by colour alone (D470).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { Link, useNavigate } from "react-router";

import { coreApi } from "@/api/endpoints";
import { queryKeys } from "@/api/queryKeys";
import type { Notification } from "@/api/types";
import { QueryState } from "@/components/ui";
import { useTranslation, type TranslationKey } from "@/i18n";
import { cn } from "@/lib/cn";
import { formatShortDate, formatTimeOfDay, localDayIso, todayIso } from "@/lib/format";
import { useAuthStore } from "@/store/auth";

import { PANEL_GROUPS, panelGroupOf, type PanelGroup } from "./notificationKinds";

const PANEL_ID = "k-notifications-panel";

const GROUP_LABEL: Record<PanelGroup, TranslationKey> = {
  clients: "shell.notifications.group.clients",
  operators: "shell.notifications.group.operators",
  tasks: "shell.notifications.group.tasks",
  system: "shell.notifications.group.system",
};

const GROUP_DOT: Record<PanelGroup, string> = {
  clients: "bg-accent",
  operators: "bg-dot-warning",
  tasks: "bg-dot-warning",
  system: "bg-dot-muted",
};

function whenLabel(iso: string): string {
  return localDayIso(iso) === todayIso() ? formatTimeOfDay(iso) : formatShortDate(iso);
}

export function NotificationBell(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement | null>(null);

  // Notifications belong to an agency. A platform admin with no agency has none to read,
  // and the API refuses the call, so the bell does not ask.
  const hasAgency = useAuthStore((state) => Boolean(state.user?.tenant));
  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => coreApi.unreadCount(),
    refetchInterval: 60_000,
    enabled: hasAgency,
  });

  // The first page only: the bell is a glance at what is new, not an archive.
  const list = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () => coreApi.notifications(),
    enabled: open,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
  };

  const markRead = useMutation({
    mutationFn: (id: number) => coreApi.markNotificationRead(id),
    onSuccess: refresh,
  });

  const markGroupRead = useMutation({
    mutationFn: (ids: readonly number[]) => Promise.all(ids.map((id) => coreApi.markNotificationRead(id))),
    onSuccess: refresh,
  });

  // A dropdown that cannot be dismissed is a trap on a touch screen, and Escape is
  // what a keyboard user reaches for first.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    function onPointerDown(event: PointerEvent): void {
      const root = container.current;
      if (root !== null && event.target instanceof Node && !root.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return (): void => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const unreadCount = unread.data?.unread ?? 0;
  const criticalCount = unread.data?.critical ?? 0;
  // Critical rows (TZ §2.5) first, in the order the server gave them: a stable sort keeps the
  // newest-first order inside each group.
  const items: readonly Notification[] = [...(list.data?.results ?? [])].sort(
    (left, right) => Number(right.is_critical) - Number(left.is_critical),
  );

  /** Reading a notification is opening it: marking it read on the side would leave the
   *  badge counting items the person has already acted on. */
  function openNotification(item: Notification): void {
    if (!item.is_read) {
      markRead.mutate(item.id);
    }
    if (item.url !== "") {
      setOpen(false);
      void navigate(item.url);
    }
  }

  return (
    <div className="relative shrink-0" ref={container}>
      <button
        type="button"
        className="relative inline-flex size-9 items-center justify-center rounded-control text-ink-muted transition-colors duration-fast ease-token hover:bg-surface-muted"
        aria-label={t("common.notifications")}
        title={criticalCount > 0 ? t("common.criticalCount", { count: criticalCount }) : t("common.notifications")}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <Bell className="size-4" strokeWidth={1.5} aria-hidden="true" />
        {unreadCount > 0 ? (
          <span className={cn("absolute top-1.75 right-2 size-1.75 rounded-pill outline-2 outline-canvas", criticalCount > 0 ? "bg-dot-danger" : "bg-accent")}>
            <span className="sr-only">{unreadCount}</span>
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          id={PANEL_ID}
          role="group"
          aria-label={t("common.notifications")}
          className="absolute top-10.5 -right-11 z-40 max-h-[70vh] w-90 max-w-[calc(100vw-24px)] overflow-y-auto rounded-card border border-line bg-surface shadow-overlay [animation:k-in_180ms_ease-out] sm:right-0"
        >
          <div className="flex h-11 items-center border-b border-surface-muted px-3.5 text-sm font-semibold">{t("common.notifications")}</div>
          <QueryState
            isPending={list.isPending}
            isError={list.isError}
            skeleton={{ shape: "list", rows: 4 }}
            isEmpty={items.length === 0}
            emptyLabel={t("common.noNotifications")}
            onRetry={() => {
              void list.refetch();
            }}
          >
            {PANEL_GROUPS.map((group) => {
              const rows = items.filter((item) => panelGroupOf(item.kind) === group);
              if (rows.length === 0) return null;
              const unreadIds = rows.filter((item) => !item.is_read).map((item) => item.id);
              return (
                <section key={group} className="border-b border-surface-muted">
                  <div className="flex items-center gap-2 px-3.5 pt-2.5 pb-1">
                    <h3 className="flex-1 text-xs font-medium tracking-normal text-ink-subtle">{t(GROUP_LABEL[group])}</h3>
                    <button
                      type="button"
                      disabled={unreadIds.length === 0 || markGroupRead.isPending}
                      onClick={() => {
                        markGroupRead.mutate(unreadIds);
                      }}
                      className="h-6 rounded-small px-2 text-xs text-accent-ink hover:bg-surface-muted disabled:cursor-default disabled:text-ink-subtle disabled:hover:bg-transparent"
                    >
                      {t("shell.notifications.read")}
                    </button>
                  </div>
                  <ul>
                    {rows.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            openNotification(item);
                          }}
                          className="flex w-full gap-2.5 px-3.5 py-2 text-left hover:bg-surface-muted"
                        >
                          <span
                            className={cn(
                              "mt-1.5 size-2 shrink-0 rounded-pill",
                              item.is_read ? "bg-line" : item.is_critical ? "bg-dot-danger" : GROUP_DOT[group],
                            )}
                          />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="flex gap-2">
                              <span className="min-w-0 flex-1 text-sm font-medium">{item.title}</span>
                              <span className="text-xs whitespace-nowrap text-ink-subtle tabular-nums">{whenLabel(item.created_at)}</span>
                            </span>
                            {item.is_critical ? <span className="text-xs font-medium text-danger-ink">{t("common.criticalLabel")}</span> : null}
                            {item.body === "" ? null : <span className="text-xs text-ink-muted">{item.body}</span>}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
            <Link
              to="/profile?section=bildirishnomalar"
              onClick={() => {
                setOpen(false);
              }}
              className="block px-3.5 py-2.5 text-sm text-accent-ink hover:underline"
            >
              {t("shell.notifications.setup")}
            </Link>
          </QueryState>
        </div>
      ) : null}
    </div>
  );
}
